import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { KineticText, Reveal, SectionLabel, Counter } from "@/components/site/primitives";
import { Lightbox } from "@/components/site/Lightbox";import {
  COMPANY, IMAGES, ENTITIES, SERVICE_GROUPS, WHY_US, LEADERS,
  PROJECTS, TECH, ROADMAP, STATS, VISION, MEDIA,
} from "@/data/content";

const COLLAGE = [
  { src: "/act-rally-map.jpg", caption: "Human chain — Map of India", rot: -3, dPos: "md:top-0 md:left-[1%] md:w-[38%]", tape: "top-left", label: "Mass rally", z: 10 },
  { src: "/act-slogan-hygiene.jpg", caption: "'Hygiene is there when cleanliness is in air'", rot: 4, dPos: "md:top-[10px] md:right-[2%] md:w-[19%]", tape: "top-right", label: "Slogan", z: 20 },
  { src: "/act-slogan-swachhbharat.jpg", caption: "A student's Swachh Bharat pledge", rot: -5, dPos: "md:top-[50px] md:left-[42%] md:w-[17%]", tape: "top-left", label: "Youth pledge", z: 30 },
  { src: "/act-drawing-2.jpg", caption: "Swachhotsav 2025 poster showcase", rot: 2, dPos: "md:top-[260px] md:left-[26%] md:w-[27%]", tape: "top-right", label: "Poster contest", z: 15 },
  { src: "/media-1.jpg", caption: "In the press — Medical kits for sanitation workers", rot: 5, dPos: "md:top-[240px] md:right-[22%] md:w-[19%]", tape: "top-left", label: "In the press", press: true, z: 25 },
  { src: "/act-plantation.jpg", caption: "School plantation drive", rot: 1, dPos: "md:bottom-[10px] md:left-[3%] md:w-[26%]", tape: "top-left", label: "Green drives", z: 12 },
  { src: "/act-rally-street.jpg", caption: "Swachhata Hi Seva city procession", rot: -4, dPos: "md:bottom-[30px] md:left-[30%] md:w-[27%]", tape: "top-right", label: "City rally", z: 18 },
  { src: "/act-slogan-sanskaar.jpg", caption: "'Swabhaav Swachhata, Sanskaar Swachhata'", rot: -2, dPos: "md:bottom-[8px] md:right-[3%] md:w-[19%]", tape: "top-right", label: "Sanskaar", z: 22 },
];

const Tape = ({ where }) => {
  const base = "absolute w-14 h-4 bg-yellow-100/70 backdrop-blur-sm shadow-sm rotate-[-8deg] pointer-events-none";
  if (where === "top-left") return <span className={base + " -top-1.5 left-3"} />;
  if (where === "top-right") return <span className={base + " -top-1.5 right-3 rotate-[8deg]"} />;
  return null;
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const lightboxItems = COLLAGE.map((c) => ({ src: c.src, caption: c.caption }));

  return (
    <section ref={ref} data-testid="home-hero" className="relative pt-[110px] md:pt-[130px] pb-12 px-5 md:px-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <SectionLabel className="mb-6 md:mb-10">Urban Infrastructure · Sanitation · Digital Governance</SectionLabel>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.86] text-[15vw] md:text-[10.5vw] lg:text-[9rem] text-raritan-ink">
              <KineticText lines={["Cleaner"]} />
              <span className="block">
                <KineticText lines={["Cities,"]} delay={0.09} className="text-raritan-green" />
              </span>
              <span className="flex items-baseline flex-wrap gap-x-6">
                <KineticText lines={["Built"]} delay={0.18} />
                <span className="font-serif italic lowercase font-medium text-raritan-red tracking-normal">
                  <KineticText lines={["on data"]} delay={0.27} />
                </span>
              </span>
            </h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9 }}
            className="lg:col-span-4"
          >
            <p className="font-body text-base md:text-lg text-raritan-muted leading-relaxed border-l-2 border-raritan-red pl-5">
              {COMPANY.tagline}
            </p>
            <Link
              to="/contact"
              data-testid="hero-cta"
              className="mt-8 inline-flex items-center gap-3 bg-raritan-green text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-raritan-green-hover transition-colors group"
            >
              Partner with us
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-14 relative"
        >
          {/* Mobile: natural flex-wrap scatter. Desktop: absolute-positioned overlapping polaroids. */}
          <motion.div
            style={{ y }}
            className="relative flex flex-wrap items-start justify-center gap-4 md:block md:h-[640px]"
          >
            {COLLAGE.map((p, i) => (
              <motion.figure
                key={p.src + i}
                initial={{ opacity: 0, y: 40, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: p.rot }}
                transition={{ delay: 0.55 + i * 0.09, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ rotate: 0, scale: 1.04, zIndex: 60 }}
                onClick={() => setLightboxIdx(i)}
                data-testid={`hero-collage-${i}`}
                style={{ zIndex: p.z }}
                className={`relative bg-white p-2 md:p-2.5 pb-8 md:pb-10 shadow-[0_10px_25px_-8px_rgba(0,0,0,0.25)] hover:shadow-[0_18px_40px_-8px_rgba(0,0,0,0.35)] cursor-zoom-in transition-shadow duration-500 ${p.dPos} md:absolute origin-center max-w-[46%] md:max-w-none`}
              >
                <Tape where={p.tape} />
                <img
                  src={p.src}
                  alt={p.caption}
                  className="block w-auto max-w-full max-h-[260px] md:max-h-[300px] md:w-full md:h-auto object-contain"
                />
                <figcaption className="absolute left-0 right-0 bottom-1.5 md:bottom-2.5 px-3 flex items-center justify-between gap-2">
                  <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.15em] text-raritan-ink truncate">
                    {p.label}
                  </span>
                  <span className={`w-1.5 h-1.5 shrink-0 ${p.press ? "bg-raritan-red" : "bg-raritan-green"}`} />
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <Lightbox
        items={lightboxItems}
        index={lightboxIdx >= 0 ? lightboxIdx : -1}
        onClose={() => setLightboxIdx(-1)}
        onIndexChange={setLightboxIdx}
      />
    </section>
  );
};

const IntroBlock = () => (
  <section data-testid="home-intro" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-3">
        <SectionLabel>Who we are</SectionLabel>
      </div>
      <div className="lg:col-span-9">
        <Reveal>
          <p className="font-heading font-medium text-2xl md:text-4xl leading-[1.15] tracking-tight text-raritan-ink text-balance">
            {COMPANY.intro}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl font-body text-lg text-raritan-muted leading-relaxed">{COMPANY.intro2}</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Entities = () => (
  <section data-testid="home-entities" className="border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16">
      <SectionLabel className="mb-4">Core entities</SectionLabel>
    </div>
    <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 border-t border-raritan-border mt-8">
      {ENTITIES.map((e, i) => (
        <Reveal key={e.id} delay={i * 0.1}>
          <div className={`p-8 md:p-12 h-full ${i === 0 ? "md:border-r border-raritan-border" : ""} group hover:bg-raritan-green-soft transition-colors duration-500`}>
            <div className="h-16 mb-6 flex items-center">
              <img src={e.logo} alt={e.short} className="h-full w-auto max-w-[240px] object-contain" />
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-raritan-red">{e.role}</span>
            <h3 className="mt-5 font-heading font-bold text-2xl md:text-3xl tracking-tight text-raritan-ink">{e.short}</h3>
            <p className="mt-2 font-mono text-xs text-raritan-muted">{e.name}</p>
            <p className="mt-6 font-body text-raritan-muted leading-relaxed">{e.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Services = () => (
  <section data-testid="home-services" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel className="mb-6">What we do</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink leading-[0.95]">
            Five integrated<br />service verticals
          </h2>
        </div>
        <Link to="/services" data-testid="services-view-all" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-raritan-green">
          View all services
          <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <div className="border-t border-raritan-border">
        {SERVICE_GROUPS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <Link to="/services" className="group grid md:grid-cols-12 gap-4 items-center py-7 border-b border-raritan-border hover:px-4 transition-all duration-300">
              <span className="md:col-span-1 font-mono text-sm text-raritan-red">{s.tag}</span>
              <h3 className="md:col-span-4 font-heading font-bold text-2xl md:text-3xl tracking-tight text-raritan-ink group-hover:text-raritan-green transition-colors">{s.title}</h3>
              <p className="md:col-span-6 font-body text-raritan-muted">{s.lead}</p>
              <ArrowUpRight className="md:col-span-1 justify-self-end text-raritan-muted group-hover:text-raritan-green group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" size={22} />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="home-why" className="bg-raritan-green text-white px-5 md:px-10 py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto">
      <SectionLabel className="mb-6 [&>span]:bg-raritan-red text-white/60">Why choose Raritan</SectionLabel>
      <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] mb-16 max-w-3xl">
        Integrated solutions.<br /><span className="text-white/50">Ground-level impact.</span>
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/15">
        {WHY_US.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.07}>
            <div className="p-8 md:p-10 border-r border-b border-white/15 h-full hover:bg-white/5 transition-colors">
              <span className="font-mono text-sm text-raritan-red">0{i + 1}</span>
              <h3 className="mt-5 font-heading font-bold text-xl md:text-2xl">{w.title}</h3>
              <p className="mt-3 font-body text-white/70 leading-relaxed">{w.desc}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.3}>
          <div className="p-8 md:p-10 border-r border-b border-white/15 h-full flex items-end">
            <p className="font-serif italic text-2xl text-white/90 leading-snug">One ecosystem, complete support.</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section data-testid="home-stats" className="px-5 md:px-10 py-20 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 0.08}>
          <div className="border-t-2 border-raritan-red pt-5">
            <Counter value={s.value} suffix={s.suffix} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-raritan-green block" />
            <p className="mt-3 font-body text-sm text-raritan-muted">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const LeadershipPreview = () => (
  <section data-testid="home-leadership" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel className="mb-6">Leadership</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink leading-[0.95]">Directors at<br />the helm</h2>
        </div>
        <Link to="/leadership" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-raritan-green">
          Meet the team <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-px bg-raritan-border border border-raritan-border">
        {LEADERS.map((l, i) => (
          <Reveal key={l.name} delay={i * 0.1}>
            <div className="bg-raritan-surface p-8 h-full group hover:bg-raritan-green-soft transition-colors duration-500">
              <div className="relative w-full aspect-[4/5] overflow-hidden border border-raritan-border mb-6">
                <img
                  src={l.photo}
                  alt={l.name}
                  style={{ objectPosition: l.objectPos || "center top", transform: l.zoom ? `scale(${l.zoom})` : undefined, transformOrigin: l.origin || "center" }}
                  className={`w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ${l.zoom ? "" : "scale-100 group-hover:scale-105"}`}
                />
              </div>
              <h3 className="font-heading font-bold text-xl text-raritan-ink">{l.name}</h3>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-raritan-red">{l.title}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProjectsPreview = () => (
  <section data-testid="home-projects" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel className="mb-6">Projects & experience</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink leading-[0.95]">On the<br />ground</h2>
        </div>
        <Link to="/projects" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-raritan-green">
          All engagements <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-px bg-raritan-border border border-raritan-border">
        {PROJECTS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.07}>
            <div className="bg-raritan-surface p-8 md:p-10 h-full group hover:bg-raritan-green hover:text-white transition-colors duration-500">
              <div className="flex justify-between items-start">
                <span className="font-mono text-sm text-raritan-red">{p.id}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-raritan-muted group-hover:text-white/60">{p.state}</span>
              </div>
              <h3 className="mt-5 font-heading font-bold text-2xl tracking-tight">{p.name}</h3>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-raritan-green group-hover:text-white">{p.area}</p>
              <p className="mt-4 font-body text-raritan-muted group-hover:text-white/80 leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Technology = () => (
  <section data-testid="home-technology" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <SectionLabel className="mb-6">Technology stack</SectionLabel>
        <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-5xl text-raritan-ink leading-[0.95] mb-6">
          Smart systems, real-time accountability
        </h2>
        <p className="font-body text-lg text-raritan-muted leading-relaxed">
          We embed advanced digital systems into urban management and sanitation operations — building transparent, efficient, data-driven governance aligned with national missions.
        </p>
      </div>
      <div className="lg:col-span-7">
        <div className="grid sm:grid-cols-2 border-t border-l border-raritan-border">
          {TECH.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.06}>
              <div className="p-7 border-r border-b border-raritan-border h-full hover:bg-raritan-green-soft transition-colors">
                <span className="font-heading font-black text-3xl text-raritan-green">{t.name}</span>
                <p className="mt-2 font-body text-sm text-raritan-muted">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const RoadmapPreview = () => (
  <section data-testid="home-roadmap" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto">
      <SectionLabel className="mb-6">Future roadmap</SectionLabel>
      <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink leading-[0.95] mb-16">Towards smarter<br />& cleaner cities</h2>
      <div className="grid md:grid-cols-4 gap-px bg-raritan-border border border-raritan-border">
        {ROADMAP.map((r, i) => (
          <Reveal key={r.no} delay={i * 0.08}>
            <div className="bg-raritan-surface p-7 h-full">
              <span className="font-mono text-sm text-raritan-red">{r.no}</span>
              <h3 className="mt-4 font-heading font-bold text-xl text-raritan-ink">{r.title}</h3>
              <p className="mt-3 font-body text-sm text-raritan-muted leading-relaxed">{r.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MediaPreview = () => (
  <section data-testid="home-media" className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel className="mb-6">Raritan in media</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink leading-[0.95]">
            Stories from<br />the field
          </h2>
        </div>
        <Link to="/media" data-testid="media-view-all" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-raritan-green">
          View all press <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-px bg-raritan-border border border-raritan-border">
        {MEDIA.slice(0, 3).map((m, i) => (
          <Reveal key={m.id} delay={i * 0.08}>
            <Link to="/media" className="block bg-raritan-surface p-6 h-full group hover:bg-raritan-green-soft transition-colors duration-500">
              <div className="relative aspect-[4/3] overflow-hidden border border-raritan-border bg-white mb-5">
                <img
                  src={m.image}
                  alt={m.title}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-raritan-red">{m.tag}</span>
              <h3 className="mt-3 font-heading font-bold text-lg text-raritan-ink leading-snug">{m.title}</h3>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-raritan-muted">{m.publication} · {m.period}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="home-cta" className="relative px-5 md:px-10 py-28 md:py-40 overflow-hidden bg-raritan-ink">
    <div className="absolute inset-0 opacity-25">
      <img src={IMAGES.green} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-[1400px] mx-auto text-center">
      <Reveal>
        <p className="font-serif italic text-2xl md:text-3xl text-white/80 mb-6">{VISION.split(" that ")[0]}.</p>
        <h2 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-8xl text-white leading-[0.9]">
          Let&apos;s build<br /><span className="text-raritan-red">together.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="cta-contact-btn"
          className="mt-12 inline-flex items-center gap-3 bg-white text-raritan-green px-9 py-5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-raritan-green-soft transition-colors group"
        >
          Start a conversation
          <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <IntroBlock />
      <Entities />
      <Services />
      <WhyUs />
      <Stats />
      <LeadershipPreview />
      <ProjectsPreview />
      <Technology />
      <RoadmapPreview />
      <MediaPreview />
      <CTA />
    </div>
  );
}
